import isEqual from "lodash.isequal";
import semver from "semver";
import { ulid } from "ulid";
import { registerSW } from "virtual:pwa-register";
import { useEffect, useRef, useState } from "preact/hooks";

import { internalEmit, internalSubscribe } from "./lib/eventEmitter";

import { modalController } from "./controllers/modals/ModalController";
import { APP_VERSION } from "./version";

const INTERVAL_HOUR = 36e5;

let forceUpdate = false;
let registration: ServiceWorkerRegistration | undefined;

export const updateSW = registerSW({
    async onNeedRefresh() {
        if (forceUpdate) {
            updateSW(true);
        } else {
            const remote = await fetchRemoteVersion();
            if (remote !== null && semver.neq(APP_VERSION, remote)) {
                internalEmit("PWA", "update");
            }
        }
    },
    onOfflineReady() {
        console.info("Ready to work offline.");
        // show a ready to work offline to user
    },
    onRegistered(r) {
        registration = r;

        // Check for updates every hour
        setInterval(() => r!.update(), INTERVAL_HOUR);
    },
});

let currentPollRate: number;
let scheduledTask: number;

/**
 * Schedule version checker
 * @param poll_rate Set poll rate in milliseconds
 */
function schedule(poll_rate = INTERVAL_HOUR) {
    if (poll_rate !== currentPollRate) {
        currentPollRate = poll_rate;
        clearInterval(scheduledTask);
        scheduledTask = setInterval(
            checkVersion,
            poll_rate,
        ) as unknown as number;
    }
}

let currentAlert: SystemAlert | undefined;
type SystemAlert = {
    text: string;
    dismissable?: boolean;

    // Теперь сообщению можно прописать время жизни

    timeout?: number;
    actions?: {
        text: string;
        type: "internal" | "external";
        href: string;
    }[];
};

/**
 * Get the current system alert
 */
export function useSystemAlert() {
    const [alert, setAlert] = useState(currentAlert);
    const clearRef = useRef<number | undefined>();
    useEffect(() => {
        function processAlert(alert: SystemAlert) {
            clearTimeout(clearRef.current);
            setAlert(alert);

            if (typeof alert.timeout === "number") {
                clearRef.current = window.setTimeout(
                    () => setAlert(undefined),
                    alert.timeout,
                );
            }
        }

        return internalSubscribe(
            "System",
            "alert",
            processAlert as (arg: unknown) => void,
        );
    }, []);
    return alert;
}

function getNocacheParam(): string {
    const LETTERS = "0123456789abcdef";
    const N = 4;
    const ret = [];
    for (let i = 0; i < N * 2; i++) {
        const pos = Math.floor(Math.random() * LETTERS.length);
        ret.push(LETTERS[pos]);
    }
    return ret.join("");
}
async function fetchRemoteVersion(): Promise<string | null> {
    try {
        const resp = await fetch(
            `${
                new URL(import.meta.env.VITE_API_URL).origin
            }/revite_version.txt` + `?nocache=${getNocacheParam()}`,
        );
        if (resp.status !== 200) {
            throw new Error(
                `Revite update url returned bad status: ${resp.status}`,
            );
        }
        return (await resp.text()).trim();
    } catch (e) {
        console.warn("Failed to fetch revite version:", e);
        return null;
    }
}

/**
 * Check whether the client is out of date
 */
async function checkVersion() {
    const alert = {
        text: "Доступна новая версия приложения! Пожалуйста, нажмите зеленую стрелку для обновления.",
        dismissable: false,
    } as SystemAlert;

    const remote = await fetchRemoteVersion();
    if (remote !== null && semver.neq(APP_VERSION, remote)) {
        // Apply any active alerts
        if (!isEqual(alert, currentAlert)) {
            currentAlert = alert;
            internalEmit("System", "alert", alert);
        }

        // Let the worker know we should immediately refresh
        forceUpdate = true;

        // Prompt service worker to update
        registration?.update();

        // Push information that the client is out of date
        modalController.push({
            key: ulid(),
            type: "out_of_date",
            version: remote,
        });
    } else {
        forceUpdate = false;
        console.info(
            `Revite update: local = ${APP_VERSION}, remote = ${remote} (up to date)`,
        );
    }
}

// Check updates only when deployed to prod
if (window.location.origin === "https://revolt.opencode.su") {
    // Check for critical updates hourly
    schedule();
    checkVersion();
}
